"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAgentApiSendMessage = useAgentApiSendMessage;
exports.useAgentApiInvokeQuery = useAgentApiInvokeQuery;
// src/apis/agent-api/hooks/useSendMessage.ts
const recoil_1 = require("recoil");
const shared_1 = require("@heartcheck/shared");
const state_1 = require("../state");
/* ------------------------------------------
   Helpers
-------------------------------------------*/
function base64UrlDecode(input) {
    try {
        const part = input.replace(/-/g, "+").replace(/_/g, "/");
        const pad = part + "===".slice((part.length + 3) % 4);
        return typeof atob === "function" ? atob(pad) : Buffer.from(pad, "base64").toString("utf8");
    }
    catch {
        return "";
    }
}
function extractUserIdFromToken(idToken) {
    if (!idToken || typeof idToken !== "string")
        return null;
    try {
        const payload = base64UrlDecode(idToken.split(".")[1] ?? "");
        const json = JSON.parse(payload || "{}");
        return typeof json.sub === "string" ? json.sub : null;
    }
    catch {
        return null;
    }
}
const truncate = (str, max = 4000) => typeof str === "string" && str.length > max ? str.slice(0, max) : str;
function isValidConversationEventPayload(obj) {
    if (!obj || typeof obj !== "object")
        return false;
    const keys = ["message", "innerDialog", "actionRequested", "actionResult"];
    return keys.some((key) => typeof obj[key] === "string" && obj[key].trim().length > 0);
}
function isValidEvent(e) {
    try {
        return (typeof e?.id === "string" &&
            typeof e?.timestamp === "string" &&
            typeof e?.conversationId === "string" &&
            typeof e?.sender === "string" &&
            typeof e?.agentId === "string" &&
            !!(e?.event?.message ||
                e?.event?.innerDialog ||
                e?.event?.actionRequested ||
                e?.event?.actionResult));
    }
    catch {
        return false;
    }
}
function hasAgentEvent(list) {
    return list.some((ev) => ev?.sender === "agent");
}
function safeTimestamp(t) {
    if (!t)
        return 0;
    const n = Date.parse(t);
    return Number.isFinite(n) ? n : 0;
}
/**
 * Extract the most likely agentId for a conversation from the metadata store.
 */
function agentIdFromConversationMeta(store, chatId) {
    const conv = store?.[chatId];
    if (!conv)
        return null;
    if (typeof conv.activeAgentId === "string" && conv.activeAgentId.length > 0) {
        return conv.activeAgentId;
    }
    const entries = Object.entries(conv.byAgent ?? {});
    if (entries.length === 0)
        return null;
    let bestId = null;
    let bestTs = -1;
    for (const [aid, meta] of entries) {
        if (!meta)
            continue;
        const ts = typeof meta.updatedAt === "string"
            ? Date.parse(meta.updatedAt) || 0
            : typeof meta.lastUpdatedAt === "number"
                ? meta.lastUpdatedAt
                : 0;
        if (ts > bestTs) {
            bestTs = ts;
            bestId = aid;
        }
    }
    return bestId;
}
/** Build a fresh GraphQL query each time with a salt comment (avoid proxy/middleware dedupe) */
function buildLoadEventsQuery(salt) {
    return new shared_1.GraphqlQuery(`
    # poll-salt: ${salt}
    query EventsByConversation($conversationId: ID!) {
      eventsByConversationId(conversationId: $conversationId) {
        id
        conversationId
        sender
        timestamp
        agentId
        userId
        event {
          message
          innerDialog
          actionRequested
          actionResult
        }
      }
    }
  `, true // -> useIdToken (proxy validates), upstream is signed with IAM in the proxy
    );
}
function extractGraphQLErrors(err) {
    if (!err)
        return [];
    const pools = [];
    if (Array.isArray(err?.errors))
        pools.push(err.errors);
    if (Array.isArray(err?.graphQLErrors))
        pools.push(err.graphQLErrors);
    if (Array.isArray(err?.response?.errors))
        pools.push(err.response.errors);
    if (Array.isArray(err?.data?.errors))
        pools.push(err.data.errors);
    const out = [];
    pools.forEach((arr) => {
        (arr || []).forEach((e) => out.push({
            message: typeof e?.message === "string" ? e.message : undefined,
            errorType: typeof e?.errorType === "string" ? e.errorType : undefined,
        }));
    });
    if (out.length === 0 && typeof err?.message === "string") {
        out.push({ message: err.message, errorType: undefined });
    }
    return out;
}
function isWelcomePending(err) {
    const errs = extractGraphQLErrors(err);
    if (errs.length === 0)
        return false;
    return errs.some((e) => (e.errorType || "").toUpperCase() === "WELCOME_PENDING" ||
        (e.message || "").toUpperCase().includes("WELCOME_PENDING"));
}
function isAgentMismatch(err) {
    const errs = extractGraphQLErrors(err);
    if (errs.length === 0)
        return false;
    return errs.some((e) => (e.errorType || "").toLowerCase().includes("agent_mismatch") ||
        (e.message || "").toLowerCase().includes("agent mismatch") ||
        (e.message || "").toLowerCase().includes("invalid agent"));
}
function makeClientMessageId(prefix, chatId, userId) {
    // Best-effort idempotency key; unique but compact
    const rnd = (typeof crypto !== "undefined" && "randomUUID" in crypto && crypto.randomUUID()) ||
        Math.random().toString(36).slice(2) + Date.now().toString(36);
    return `${prefix}:${chatId}:${userId}:${rnd}`;
}
/* ------------------------------------------
   Mutations/Queries (private API via proxy)
-------------------------------------------*/
const sendConversationMessageMutation = new shared_1.GraphqlQuery(`
  mutation SendMessage(
    $conversationId: ID!,
    $event: UserAction!,
    $conversation: ConversationInput!,
    $clientMessageId: ID
  ) {
    userPublishMessage(
      conversationId: $conversationId,
      event: $event,
      conversation: $conversation,
      clientMessageId: $clientMessageId
    ) {
      id
      conversationId
      userId
      sender
      timestamp
      agentId
      event {
        message
        innerDialog
        actionRequested
        actionResult
      }
    }
  }
  `, true);
const getConversationAgentQuery = new shared_1.GraphqlQuery(`
  query GetConversationAgent($id: ID!) {
    conversationById(id: $id) {
      id
      agent
    }
  }
`, true);
function useAgentApiSendMessage() {
    const setConversationEvents = (0, recoil_1.useSetRecoilState)(state_1.ConversationEvents);
    const metadataStore = (0, recoil_1.useRecoilValue)(state_1.ConversationPartialResults);
    const mergeEventsForChat = (chatId, incoming) => {
        if (!Array.isArray(incoming) || incoming.length === 0)
            return;
        setConversationEvents((prev) => {
            const current = prev[chatId]?.value ?? [];
            const byId = new Map();
            current.forEach((e) => e?.id && byId.set(e.id, e));
            incoming.forEach((e) => e?.id && byId.set(e.id, e));
            const merged = Array.from(byId.values())
                .filter(isValidEvent)
                .sort((a, b) => safeTimestamp(a.timestamp) - safeTimestamp(b.timestamp));
            return { ...prev, [chatId]: state_1.Loadable.loaded(merged) };
        });
    };
    const burstPollAfterSend = async (chatId) => {
        // Short burst to catch welcome / first tokens quickly
        const schedule = [0, 300, 700, 1200, 2000];
        for (let i = 0; i < schedule.length; i++) {
            await new Promise((r) => setTimeout(r, schedule[i]));
            try {
                const q = buildLoadEventsQuery(String(Date.now()));
                const res = await q.invoke({ conversationId: chatId });
                const raw = res?.eventsByConversationId ?? [];
                const valid = raw.filter(isValidEvent);
                mergeEventsForChat(chatId, valid);
                if (valid.length >= 2 && hasAgentEvent(valid))
                    break;
            }
            catch (err) {
                if (isWelcomePending(err))
                    continue;
                console.error("[sendMessage] burst poll failed:", err);
            }
        }
    };
    const fetchAuthoritativeAgent = async (chatId) => {
        try {
            const res = await getConversationAgentQuery.invoke({ id: chatId });
            const agent = res?.conversationById?.agent;
            if (typeof agent === "string" && agent.length > 0)
                return agent;
        }
        catch {
            // fall through to events-based inference
        }
        try {
            const q = buildLoadEventsQuery(String(Date.now()));
            const res = await q.invoke({ conversationId: chatId });
            const events = (res?.eventsByConversationId ?? [])
                .filter(isValidEvent)
                .sort((a, b) => safeTimestamp(a.timestamp) - safeTimestamp(b.timestamp));
            if (events.length > 0) {
                const lastAgent = [...events].reverse().find((e) => e.sender === "agent") ?? events[events.length - 1];
                if (lastAgent?.agentId)
                    return lastAgent.agentId;
            }
        }
        catch {
            // ignore
        }
        return null;
    };
    const actuallySend = async (payload) => {
        const result = await sendConversationMessageMutation.invoke(payload);
        if (!result?.userPublishMessage) {
            throw new Error("No userPublishMessage returned in GraphQL response");
        }
        setConversationEvents((prev) => {
            const current = prev[payload.conversationId];
            const previous = current?.isLoaded() ? current.value ?? [] : [];
            const already = previous.some((m) => m.id === result.userPublishMessage.id);
            const next = already ? previous : [...previous, result.userPublishMessage];
            return { ...prev, [payload.conversationId]: state_1.Loadable.loaded(next) };
        });
        return result;
    };
    return async ({ chatId, event, agentId, clientMessageId }) => {
        if (!chatId)
            throw new Error("❌ Missing chatId when sending message");
        // Token-first identity (no reliance on Amplify CognitoUser/getUsername)
        const idToken = await (0, shared_1.getIdTokenWithFallback)();
        const userId = extractUserIdFromToken(idToken);
        if (!userId)
            throw new Error("❌ Missing or invalid userId from ID token payload");
        if (!isValidConversationEventPayload(event)) {
            throw new Error("❌ Invalid conversation event payload structure");
        }
        // Normalize & limit payload sizes
        const enrichedEvent = {
            message: typeof event.message === "string"
                ? (truncate(event.message.trim(), 4000) || undefined)
                : undefined,
            innerDialog: typeof event.innerDialog === "string"
                ? (truncate(event.innerDialog.trim(), 4000) || undefined)
                : undefined,
            actionRequested: typeof event.actionRequested === "string"
                ? truncate(event.actionRequested, 8000)
                : undefined,
            actionResult: truncate(event.actionResult, 12000),
        };
        let serverAgentId = await fetchAuthoritativeAgent(chatId);
        const fromMeta = agentIdFromConversationMeta(metadataStore, chatId);
        let resolvedAgentId = serverAgentId || agentId || fromMeta || null;
        if (!resolvedAgentId) {
            throw new Error("❌ Unable to resolve agent for this conversation.");
        }
        // If server disagrees with the caller-provided agent, prefer server
        if (agentId && serverAgentId && agentId !== serverAgentId) {
            resolvedAgentId = serverAgentId;
        }
        const payload = {
            conversationId: chatId,
            event: enrichedEvent,
            conversation: {
                id: chatId,
                agent: resolvedAgentId,
                userId,
            },
            clientMessageId: clientMessageId ??
                makeClientMessageId("msg", chatId, userId),
        };
        try {
            const result = await actuallySend(payload);
            void burstPollAfterSend(chatId);
            return result;
        }
        catch (err) {
            if (isAgentMismatch(err)) {
                // Retry with server-authoritative agent if we can fetch one now
                try {
                    serverAgentId = await fetchAuthoritativeAgent(chatId);
                    if (serverAgentId && serverAgentId !== resolvedAgentId) {
                        const retryPayload = {
                            ...payload,
                            conversation: { ...payload.conversation, agent: serverAgentId },
                        };
                        const result = await actuallySend(retryPayload);
                        void burstPollAfterSend(chatId);
                        return result;
                    }
                }
                catch {
                    // fall through to surface original error
                }
            }
            if (err?.response?.errors) {
                // Useful for diagnosing auth mode issues upstream
                // eslint-disable-next-line no-console
                console.error("🛑 AppSync GraphQL errors:", JSON.stringify(err.response.errors, null, 2));
            }
            // eslint-disable-next-line no-console
            console.error("❌ Failed to send message:", err);
            throw err;
        }
    };
}
/* ------------------------------------------
   Optional helper to run arbitrary queries
-------------------------------------------*/
function useAgentApiInvokeQuery() {
    const sendMessage = useAgentApiSendMessage();
    const metadataStore = (0, recoil_1.useRecoilValue)(state_1.ConversationPartialResults);
    return async (conversationId, query, variables, agentId) => {
        if (!conversationId)
            throw new Error("❌ Missing conversationId in invoke query");
        const inferredAgent = agentIdFromConversationMeta(metadataStore, conversationId);
        const explicitAgent = agentId ?? inferredAgent ?? undefined;
        try {
            const dynamicQuery = new shared_1.GraphqlQuery(query, true);
            const result = await dynamicQuery.invoke(variables);
            await sendMessage({
                chatId: conversationId,
                event: {
                    actionRequested: query,
                    actionResult: truncate(JSON.stringify(result), 12000),
                },
                agentId: explicitAgent,
            });
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error("❌ Invocation failed:", err);
            alert(`Invocation failed. ${err instanceof Error ? err.message : "See console for details."}`);
        }
    };
}
